import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Box, Grid, Typography } from "@mui/material"
import { useFormik } from 'formik';
import { validationsClinicSchema } from "../../../validations/validationSignUpSchema";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import BaseColorText from "../../BaseColorText";
import BaseButton from "../../BaseButton";
import BaseInput from "../../BaseInput";
import HelpText from "../../HelpText";
import { setStorage } from "../../../hooks/use-local-storage";
import { useMutation } from "@apollo/client";
import { PhysicianRegister } from "../../../apollo-client/api-query";
import { showErrorToast } from "../../../utils/toast";
import { GENDERS_ENUM, STATES } from "../../../constants";

/**
 * @param {Object} props
 */
const ClinicForm = ({ infoFormStates, kitCode = '' }) => {
  const navigate = useNavigate()
  const [registerPhysician, { loading }] = useMutation(PhysicianRegister)

  const [isOpenStateDropdown, setIsOpenStateDropdown] = useState(false)
  const [stateList, setStateList] = useState(STATES)

  const { errors, handleBlur, handleChange, handleSubmit, touched, values, setValues } =
    useFormik({
      validateOnBlur: true,
      initialValues: {
        clinicAddressLine1: '',
        clinicAddressLine2: null,
        clinicName: '',
        clinicAddressCity: '',
        clinicAddressState: '',
        clinicAddressPostalCode: '',
        clinicAddressPostalCountry: "USA"
      },
      validationSchema: validationsClinicSchema,
      onSubmit: (values, { setSubmitting }) => {
        registerPhysician({
          variables: {
            physicianCode: kitCode,
            physicianData: {
              firstName: infoFormStates?.firstName,
              lastName: infoFormStates?.lastName,
              // middleName: '', Need to add middleName in the form or not send data at all since it breaks API
              sex: GENDERS_ENUM.MALE,
              dateOfBirth: "1993-01-01", // Obvious fake date of birth for time being until we add date of birth in the form
              staffProfile: {
                phone: infoFormStates?.phone,
                npi: infoFormStates?.npi,
                specialty: infoFormStates?.specialty,
                title: infoFormStates?.title,
                bio: infoFormStates?.bio,
                ...values
              }
            },
          }
        })
        .then(() => {
          setStorage('showSignUpSuccessModal', 'show')
          navigate('/')
        })
        .catch((error) => {
          return showErrorToast({
            message: error.message
          })
        })
        .finally(() => {
          
        })
      },
    });

  const handleChangeState = (e) => {
    handleChange(e)
    const stateArray = STATES.filter(item => item.includes(e.target.value))
    setStateList(stateArray)

    if (e.target.value) {
      setIsOpenStateDropdown(true)
    } else {
      setIsOpenStateDropdown(false)
    }
    if (stateArray.length === 0) setIsOpenStateDropdown(false)
  }

  const handleClickState = (state) => {
    setIsOpenStateDropdown(false)
    setValues({
      ...values,
      clinicAddressState: state
    })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%'
      }}
    >
      <Box sx={{
        width: '100%',
        mt: '70px'
      }}>
        <Typography variant="h1">
          Your <BaseColorText isGradient gradientColor='#5A3BC7 30%, #5FB2EC 90%'>clinic information.</BaseColorText>
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: '#46545F',
            mt: '20px'
          }}
        >
          This tool is strictly for dDNA Physicians and Administrators only.
        </Typography>
        <Box sx={{
          mt: '30px',
        }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={'16px'}>
              <Grid item xs={12}>
                <BaseInput
                  fullWidth
                  label='Address line'
                  name='clinicAddressLine1'
                  type='text'
                  placeholder='Type address'
                  value={values.clinicAddressLine1 || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.clinicAddressLine1 ? errors.clinicAddressLine1 : ""}
                  error={touched.clinicAddressLine1 && Boolean(errors.clinicAddressLine1)}
                  required
                />
              </Grid>
              <Grid item md={4}>
                <BaseInput
                  fullWidth
                  label='City'
                  name='clinicAddressCity'
                  type='text'
                  placeholder='City'
                  value={values.clinicAddressCity || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.clinicAddressCity ? errors.clinicAddressCity : ""}
                  error={touched.clinicAddressCity && Boolean(errors.clinicAddressCity)}
                  required
                />
              </Grid>
              <Grid item md={4}>
                <Box
                  sx={{
                    position: 'relative'
                  }}
                >
                  <BaseInput
                    fullWidth
                    label='State'
                    name='clinicAddressState'
                    type='text'
                    placeholder='State'
                    value={values.clinicAddressState || ''}
                    onChange={handleChangeState}
                    onFocus={() => setIsOpenStateDropdown(true)}
                    onBlur={handleBlur}
                    helperText={touched.clinicAddressState ? errors.clinicAddressState : ""}
                    error={touched.clinicAddressState && Boolean(errors.clinicAddressState)}
                    required
                    inputStyles={{
                      paddingRight: '10px'
                    }}
                  />
                  <ExpandMoreIcon 
                    sx={{
                      position: 'absolute',
                      right: '1px',
                      top: '36px',
                      color: 'primary.mediumGray',
                      zIndex: 10,
                      cursor: 'pointer'
                    }}
                    onClick={() => setIsOpenStateDropdown(!isOpenStateDropdown)}
                  />
                  {isOpenStateDropdown && (
                    <Box
                      className="scroll-lightGray"
                      sx={{
                        position: 'absolute',
                        top: '80px',
                        left: 0,
                        minWidth: '200px',
                        height: '300px',
                        overflowY: 'auto',
                        bgcolor: 'white',
                        borderRadius: '8px',
                        pr: '1px',
                        border: '1px solid',
                        borderColor: 'primary.mainBlue',
                        padding: '8px 2px 8px 8px',
                        boxShadow: '0px 2px 10px 0px #5B87EF33',
                        zIndex: 10
                      }}
                    >
                      {stateList.map((stateVal => (
                        <Box
                          key={stateVal}
                          sx={{
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            bgcolor: values.clinicAddressState === stateVal ? 'primary.lightBlue' : 'white',
                            '&:hover': {
                              bgcolor: 'primary.lightBlue'
                            }
                          }}
                          onClick={() => handleClickState(stateVal)}
                        >
                          <Typography variant="body" color="primary.darkGray">
                            {stateVal}
                          </Typography>
                        </Box>
                      )))}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item md={4}>
                <BaseInput
                  fullWidth
                  label='Zip Code'
                  name='clinicAddressPostalCode'
                  type='text'
                  placeholder='Zip'
                  value={values.clinicAddressPostalCode || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.clinicAddressPostalCode ? errors.clinicAddressPostalCode : ""}
                  error={touched.clinicAddressPostalCode && Boolean(errors.clinicAddressPostalCode)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <BaseInput
                  fullWidth
                  label='Clinic name (optional)'
                  name='clinicName'
                  type='text'
                  placeholder='Type name'
                  value={values.clinicName || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={touched.clinicName ? errors.clinicName : ""}
                  error={touched.clinicName && Boolean(errors.clinicName)}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                mt: { xs: '30px', sm: '50px' },
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <BaseButton
                disabled={Object.entries(errors).length !== 0}
                loading={loading}
                variant='contained'
                buttonType='primary'
                type='submit'
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body">Finishing up</Typography>
                <ChevronRightIcon />
              </BaseButton>
            </Box>
          </form>
        </Box>
      </Box>
      <HelpText sx={{
        width: '100%',
        borderTop: '1px solid #ABB7C0',
        textAlign: 'center',
        pt: '33px',
        pb: { xs: 0, md: '30px' },
        mt: '80px'
      }}>
      </HelpText>
    </Box>
  )
}

export default ClinicForm;